import * as React from "react";
import { inject, observer } from "mobx-react";
import { Button, Overlay, Popover, Spinner, Table } from "react-bootstrap";
import {
    CellContext,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable
} from "@tanstack/react-table";
import { AppStore } from "Stores/appStore";
import { Attachment, AttachmentStore } from "Stores/attachmentStore";
import { AttachmentLink } from "Components/Common/AttachmentLink";
import * as _ from "lodash";
import { Ticket, TicketStore } from "Stores/dashboardStore/tickets";
import {flowResult, reaction} from "mobx";
import {Utils} from "../../common/utils";

interface SnapshotChangerProps {
    ticketStore?: TicketStore
    attachmentId: string
    onChange: (attachmentId: string | 0, isFinal: boolean, callback: () => void) => void
    showFinal: boolean
    isCurrentInitial: boolean
    isCurrentFinal: boolean
}

interface SnapshotChangerState {
    showOptions: boolean
    isUpdating: boolean
}

@inject("ticketStore")
@observer
class SnapshotChanger extends React.Component<SnapshotChangerProps, SnapshotChangerState> {

    tooltipTargetRef;

    state = {
        showOptions: false,
        isUpdating: false,
    };

    handleClick = () => {
        this.setState({showOptions: !this.state.showOptions});
    };

    doSend = (callback: () => void) => {
        this.setState({showOptions: false, isUpdating: true}, callback);
    };

    handleSetInitial = () => {
        this.doSend(() => {
            this.props.onChange(this.props.attachmentId, false, this.onChangeDone);
        });
    };

    handleSetFinal = () => {
        this.doSend(() => {
            this.props.onChange(this.props.attachmentId, true, this.onChangeDone);
        });
    };

    handleClearInitial = () => {
        this.doSend(() => {
            this.props.onChange(0, false, this.onChangeDone);
        });
    };

    handleClearFinal = () => {
        this.doSend(() => {
            this.props.onChange(0, true, this.onChangeDone);
        });
    };

    handleHide = () => {
        this.setState({showOptions: false});
    };

    onChangeDone = () => {
        this.setState({isUpdating: false});
    };

    assignRef = (ref) => this.tooltipTargetRef = ref;

    render(){
        return (
            <React.Fragment>
                <Button
                    disabled={this.state.isUpdating || !this.props.ticketStore || this.props.ticketStore!.isProcessing}
                    onClick={this.handleClick}
                    variant={"icon"}>
                    {this.state.isUpdating
                        ? <Spinner animation="border" size="sm" />
                        : <i className="icon icon-ellipsis-h padded" ref={this.assignRef}/>}
                </Button>
                <Overlay target={this.tooltipTargetRef} show={this.state.showOptions} placement="right" rootClose={true} onHide={this.handleHide}>
                    {(props) => (
                        <Popover id={`snapshot-updater-${this.props.attachmentId}`} {...props}>
                            {this.props.isCurrentInitial
                                ? <div className="attachment-option" onClick={this.handleClearInitial}>Clear As Initial Snapshot</div>
                                : <div className="attachment-option" onClick={this.handleSetInitial}>Use As Initial Snapshot</div>}
                            {this.props.showFinal
                                ? this.props.isCurrentFinal
                                    ? <div className="attachment-option" onClick={this.handleClearFinal}>Clear As Final Snapshot</div>
                                    : <div className="attachment-option" onClick={this.handleSetFinal}>Use As Final Snapshot</div>
                                : null}
                        </Popover>
                    )}
                </Overlay>
            </React.Fragment>);
    }
}

interface AttachmentsTableProps {
    actionplan: Ticket,
    onDelete: (attachmentId: string) => void,
    onSnapshotUpdate?: () => void,
    attachmentStore?: AttachmentStore,
    ticketStore?: TicketStore,
    appStore?: AppStore,
}

interface AttachmentsTableState {
    attachments: Attachment[]
}

@inject("attachmentStore", "ticketStore", "appStore")
@observer
export class AttachmentsTable extends React.Component<AttachmentsTableProps, AttachmentsTableState> {

    constructor(props) {
        super(props);
        this.state = {
            attachments: []
        };
    }
    async componentDidMount(){
        if (!this.props.attachmentStore) {
            return;
        }
        reaction(() => this.props.attachmentStore?.attachments, attachments => {
            if (attachments) {
                this.setState({attachments});
            }
        });

        await flowResult(this.props.attachmentStore.getAcceptedMimetypes());
        const attachments = await flowResult(this.props.attachmentStore.getAllAttachmentsForActionplan(this.props.actionplan.ruuid));
        if (attachments) {
            this.setState({attachments});
        }
    }

    handleDelete = (att: Attachment) => {
        this.props.appStore!.showConfirmModal({
            element: (
                <span>{ `Are you sure you want to delete ${ att.file_name }?` }</span>
            ),
            accept: () => {
                this.props.onDelete(att.id);
            },
            // cancel: () => {},
            showActions: true,
        });
    };

    handleSnapshotChange = (attachmentId: string | 0, isFinal = false, callback: () => void) => {
        const data = {
            ruuid: this.props.actionplan.ruuid,
            // To unset the value of closed_attachment_id or initial_attachment_id, the api expects to receive 0
            [isFinal ? "closed_attachment_id" : "initial_attachment_id"]: attachmentId,
        };
        this.props.ticketStore!.createOrUpdateTicket(data, Utils.getChosenSchoolInfo()).then(() => {
            callback();
            if(this.props.onSnapshotUpdate){
                this.props.onSnapshotUpdate();
            }
        });
    };

    renderAttachmentLink = (props: CellContext<Attachment, unknown>) => (<AttachmentLink attachment={ props.row.original } includeIcon={ false }/>);

    renderTypeIcon = (props: CellContext<Attachment, unknown>) => {
        const fileIcon = props.row.original.file_icon;
        const iconClass = fileIcon === "file" ? "icon-file-o" : `icon-file-${fileIcon}-o`;
        return <i className={ `icon ${iconClass}`}/>;
    };

    renderDate = (props: CellContext<Attachment, unknown>) => (<div
        title={Utils.formatDate(new Date(props.row.original.created), "MM/DD/YYYY hh:mm:ss a") }>{ Utils.formatDate(new Date(props.row.original.created), "MM/DD/YYYY") }</div>);

    renderDelete = (props: CellContext<Attachment, unknown>) => (props.row.original.permissions.can_delete
        ? <Button variant={"icon"} onClick={ () => this.handleDelete(props.row.original) }>
            <i className="icon icon-times"/>
        </Button>
        : null);

    getOptionLabel = (opt) => {
        // If types is defined, option is a group header
        if(opt.types){
            return (
                <div className="group-option">
                    <i className={ `icon icon-file${ opt.icon === "file" ? "" : `-${opt.icon}` }-o` }/>
                    <span> - { opt.icon }</span>
                </div>);
        }
        // Else option is a specific file type
        return opt.label;
    };

    getFileTypeFilter = () => {
        return ({
            filterMethod: ({value}: any, row: any) => {
                if(value.value === "all") return true;
                if(value.types){
                    const splitFile = row.file_name.split(".");
                    const fileExtension = splitFile[splitFile.length - 1];
                    return _.findIndex(value.types, (str) => str === fileExtension) > -1;
                }
                return row.file_name.endsWith(`.${ value.value }`);
            },
        });
    };

    columns: ColumnDef<Attachment>[] = [
        {
            header: "Type",
            accessorKey: "file_icon",
            cell: this.renderTypeIcon,
            size: 100,
            enableColumnFilter: false,
        },
        {
            header: "Attachment Name",
            accessorKey: "file_name",
            cell: this.renderAttachmentLink,
            size: 200,
        },
        {
            header: "Uploaded By",
            accessorKey: "uploaded_by_email",
            size: 120,
        },
        {
            header: "Upload Date",
            accessorKey: "created",
            cell: this.renderDate,
            size: 120,
            enableColumnFilter: false,
        },
        ...(this.props.actionplan.permissions.can_edit_snapshots ?
            [{
                header: () => null,
                id: "editSnapshot",
                cell: (props: CellContext<Attachment, unknown>) => (props.row.original as Attachment).file_icon === "image"
                    ? <SnapshotChanger
                        attachmentId={props.row.original.id}
                        onChange={this.handleSnapshotChange}
                        showFinal={!!(this.props.actionplan.type === "task" && this.props.actionplan.status && (this.props.actionplan.status as string).toLowerCase() === "closed")}
                        isCurrentInitial={this.props.actionplan.initial_attachment_id === props.row.original.id}
                        isCurrentFinal={this.props.actionplan.closed_attachment_id === props.row.original.id}
                    /> : null,
                minSize: 80,
            }] :[]),
        {
            header: () => null,
            id: "deleter",
            cell: this.renderDelete,
            size: 80,
        },
    ];

    renderNoDataComponent = () => <div style={{display: "none"}}/>;

    render(){
        if(!this.props.attachmentStore) return null;
        return (this.props.attachmentStore!.attachments.length === 0 && !this.props.attachmentStore!.isFetchingAttachments
                ? <div className="no-attachments-message">No Attachments</div>
                : <AttachmentsReactTable columns={this.columns} data={this.state.attachments}/>
        );
    }
}

interface AttachmentsReactTableProps {
    columns: ColumnDef<Attachment>[]
    data: Attachment[]
}

function AttachmentsReactTable({columns, data}: AttachmentsReactTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Table>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                        <th key={header.id}>
                            {header.isPlaceholder
                                ? null
                                : (
                                    <>
                                        <div>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </div>
                                    </>
                                )}
                        </th>
                    ))}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            </tbody>
        </Table>
    );
}

